import React, { useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { Upload, Download, FileSpreadsheet, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ImportarExcel({ tipo, label, onSuccess }) {
    const [open, setOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState(null);
    const fileRef = useRef(null);

    const token = localStorage.getItem('token');

    const handleDownloadPlantilla = async () => {
        try {
            const res = await fetch(`${API_URL}/api/diesel/importar/plantilla/${tipo}`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (res.ok) {
                const blob = await res.blob();
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = `plantilla_${tipo}.xlsx`;
                link.click();
                URL.revokeObjectURL(link.href);
                toast.success('Plantilla descargada');
            } else {
                toast.error('Error al descargar plantilla');
            }
        } catch {
            toast.error('Error de conexion');
        }
    };

    const handleUpload = async () => {
        const file = fileRef.current?.files?.[0];
        if (!file) { toast.error('Seleccione un archivo'); return; }

        setUploading(true);
        setResult(null);
        try {
            const formData = new FormData();
            formData.append('file', file);

            const res = await fetch(`${API_URL}/api/diesel/importar/${tipo}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData,
            });

            const data = await res.json();
            if (res.ok) {
                setResult(data);
                if (data.created > 0) {
                    toast.success(data.message);
                    onSuccess?.();
                } else {
                    toast.warning(data.message || 'No se importaron registros');
                }
            } else {
                toast.error(data.detail || 'Error al importar');
            }
        } catch {
            toast.error('Error al procesar archivo');
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        setOpen(false);
        setResult(null);
        if (fileRef.current) fileRef.current.value = '';
    };

    return (
        <>
            <Button
                variant="outline"
                size="sm"
                onClick={() => setOpen(true)}
                data-testid={`btn-importar-${tipo}`}
            >
                <Upload className="h-4 w-4 mr-2" />
                Importar
            </Button>

            <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileSpreadsheet className="h-5 w-5" style={{ color: 'var(--diesel-primary)' }} />
                            Importar {label}
                        </DialogTitle>
                        <DialogDescription>
                            Descargue la plantilla, complete los datos y suba el archivo Excel.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        {/* Paso 1: Descargar plantilla */}
                        <div className="flex items-center gap-3 p-3 border rounded-lg">
                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted font-bold text-sm">1</div>
                            <div className="flex-1">
                                <p className="text-sm font-medium">Descargar plantilla</p>
                                <p className="text-xs text-muted-foreground">Incluye ejemplo en la primera fila</p>
                            </div>
                            <Button variant="outline" size="sm" onClick={handleDownloadPlantilla} data-testid={`btn-plantilla-${tipo}`}>
                                <Download className="h-4 w-4 mr-1" />
                                .xlsx
                            </Button>
                        </div>

                        {/* Paso 2: Subir archivo */}
                        <div className="flex items-center gap-3 p-3 border rounded-lg">
                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted font-bold text-sm">2</div>
                            <div className="flex-1">
                                <p className="text-sm font-medium">Subir archivo completado</p>
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept=".xlsx,.xls"
                                    className="mt-2 text-sm w-full"
                                    data-testid={`input-file-${tipo}`}
                                />
                            </div>
                        </div>

                        {/* Resultado */}
                        {result && (
                            <div className={`p-3 rounded-lg text-sm space-y-2 ${result.errors?.length ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-emerald-500/10 border border-emerald-500/30'}`}>
                                <div className="flex items-center gap-2 font-medium">
                                    {result.created > 0 ? <CheckCircle className="h-4 w-4 text-emerald-500" /> : <AlertTriangle className="h-4 w-4 text-amber-500" />}
                                    {result.message}
                                </div>
                                {result.errors?.length > 0 && (
                                    <div className="max-h-32 overflow-y-auto space-y-1">
                                        {result.errors.map((err, i) => (
                                            <p key={i} className="text-xs text-muted-foreground">{err}</p>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={handleClose}>Cerrar</Button>
                        <Button onClick={handleUpload} disabled={uploading}>
                            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                            {uploading ? 'Importando...' : 'Importar'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
